import { useCallback, useLayoutEffect } from "react";
import { FilePlusIcon, NetworkIcon } from "lucide-react";

import { useOpenRecord } from "@/app/app/_/routing";
import { Icon } from "@/components/atoms/Icon";
import {
  NavigableCardGrid,
  navigableCardSelectionClassName,
} from "@/components/molecules/NavigableCardGrid";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { State } from "@/state";
import { formatApolloError } from "@/utils/graphqlErrors";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const COMMAND_ID = "new-rule";

/** @deprecated Hidden stack commands removed — wizard uses internal steps. */
export const PICK_RULE_TEMPLATE_COMMAND_ID = "pick-rule-template";
/** @deprecated Hidden stack commands removed — wizard uses internal steps. */
export const CONFIGURE_RULE_TEMPLATE_COMMAND_ID = "configure-rule-template";

export type NewRuleContextType = Record<string, never>;

function useNavigateToCreatedRule() {
  const openRecord = useOpenRecord();

  return useCallback(
    (ruleId: string) => {
      openRecord({ id: ruleId });
      State.getState().clearCommandStack();
    },
    [openRecord],
  );
}

function NewRuleWizard(_props: {
  commandInstanceId: string;
  commandDefinitionId: string;
  context?: NewRuleContextType;
}) {
  const getEntityIdentifier = State((state) => state.getEntityIdentifier);
  const setSnackbarMessage = State((state) => state.setSnackbarMessage);
  const navigateToCreated = useNavigateToCreatedRule();

  const { mutate: createNode, loading: creating } = useBackendMutation({
    mutation: Mutations.CREATE_NODE,
    refetchQueries: [Queries.RULES_CONNECTION_QUERY],
  });

  const handleCreateFromScratch = useCallback(() => {
    const entityIdentifier = getEntityIdentifier();
    if (!entityIdentifier) return;

    createNode({
      variables: {
        input: {
          entityIdentifier,
          node: {
            rule: {
              name: "New rule",
            },
          },
        },
      },
    })
      .then((result: any) => {
        const created = result?.data?.createNode?.data?.[0];
        if (!created?.id) return;
        navigateToCreated(created.id);
      })
      .catch((error: unknown) => {
        setSnackbarMessage(formatApolloError(error));
      });
  }, [createNode, getEntityIdentifier, navigateToCreated, setSnackbarMessage]);

  return (
    <StickyLayout>
      <NavigableCardGrid
        itemCount={1}
        minColumnWidth={280}
        getItemKey={() => "scratch"}
        isItemDisabled={() => creating}
        onActivate={() => handleCreateFromScratch()}
        renderItem={({ isActive, isFocusWithin, rootProps }) => (
          <Card
            data-testid="new-rule-from-scratch"
            {...rootProps}
            className={cn(
              rootProps.className,
              creating ? "cursor-wait opacity-70" : "cursor-pointer",
              "min-h-35",
              navigableCardSelectionClassName({
                isActive,
                isFocusWithin,
                suppressHover: creating,
              }),
            )}
          >
            <CardHeader>
              <Icon ReactIcon={FilePlusIcon} size="lg" />
              <CardTitle>Start from scratch</CardTitle>
              <CardDescription>
                {" "}
                Create a blank rule and configure when it runs, what it finds,
                and what it applies.
              </CardDescription>
            </CardHeader>
          </Card>
        )}
      />
    </StickyLayout>
  );
}

export function NewRuleCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "New rule",
      description: "Create a new rule from scratch",
      searchText: "New rule automation scratch blank create",
      getLabel: () => "New rule",
      component: NewRuleWizard,
      ReactIcon: NetworkIcon,
      group: "Objects",
      groupPriority: 90,
      isConstrained: true,
      onClick: () => {
        State.getState().pushToCommandStack({
          commandDefinitionId: COMMAND_ID,
        });
      },
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, [registerCommandDefinition, unregisterCommandDefinition]);

  return null;
}
